"use client";

import { useTransform, type MotionValue } from "framer-motion";

export type ParsedStat = {
  prefix: string;
  suffix: string;
  target: number;
  grouped: boolean;
};

/**
 * Splits a stat into a countable figure plus whatever brackets it, so "500+" and
 * "€2000" still climb.
 *
 * Only an unambiguous integer qualifies: bare digits, or German thousands
 * grouping where every "." is followed by exactly three digits. Anything else is
 * left to render as authored, because "2,5" and "2.500" cannot be told apart
 * without knowing which the editor meant — and guessing wrong would silently
 * animate to a number a hundredfold off.
 */
export function parseStat(value?: string): ParsedStat | null {
  if (!value) return null;

  const match = /^(\D*?)(\d{1,3}(?:\.\d{3})+|\d+)(\D*)$/.exec(value.trim());
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const target = Number(digits.replace(/\./g, ""));
  if (!Number.isFinite(target)) return null;

  return { prefix, suffix, target, grouped: digits.includes(".") };
}

// the page is German, so a grouped figure regroups as it climbs rather than
// spilling into an unseparated run of digits
export const formatCount = (value: number, grouped: boolean) =>
  grouped ? Math.round(value).toLocaleString("de-DE") : String(Math.round(value));

/**
 * The figure climbs with the scroll rather than on its own timer: it hangs off
 * the same slice as the card it sits in, so it can never finish before the card
 * carrying it has arrived.
 *
 * Pass `useReveal`'s `linear`, not its `reveal` — the eased curve front-loads a
 * count badly, throwing it most of the way to its target in the first quarter of
 * the window and then inching to the end.
 *
 * Returns a MotionValue meant to be a motion element's *only* child — framer
 * drives it straight onto textContent, so the number updates every frame without
 * re-rendering React.
 */
export function useCountUp(
  reveal: MotionValue<number>,
  parsed: ParsedStat | null,
) {
  const count = useTransform(reveal, [0, 1], [0, parsed?.target ?? 0]);

  return useTransform(count, (value) =>
    formatCount(value, parsed?.grouped ?? false),
  );
}
